import React, { useState } from 'react';
import { Box, TextField, Typography, Button, IconButton, Card, CardContent, Grid, FormControlLabel, Checkbox } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { FormComponentProps, BusinessRentalData } from '../../types/forms';

interface BusinessRentalProperty {
  id: number;
  propertyType: 'business' | 'rental';
  propertyName: string;
  propertyAddress: string;
  serviceDate: string;
  annualIncome: string;
  expenses: {
    advertising: boolean;
    travel: boolean;
    maintenance: boolean;
    utilities: boolean;
  };
  additionalExpenses: string;
}

const BusinessRental: React.FC<FormComponentProps<BusinessRentalData>> = ({ data, onNext }) => {
  const [properties, setProperties] = useState<BusinessRentalProperty[]>(() => {
    const initialProperties: BusinessRentalProperty[] = [];
    if (data?.businessName) {
      initialProperties.push({
        id: Date.now(),
        propertyType: 'business',
        propertyName: data.businessName,
        propertyAddress: '',
        serviceDate: '',
        annualIncome: '',
        expenses: {
          advertising: false,
          travel: false,
          maintenance: false,
          utilities: false,
        },
        additionalExpenses: '',
      });
    }
    if (data?.rentalProperties?.length) {
      data.rentalProperties.forEach(prop => {
        initialProperties.push({
          id: Date.now() + Math.random(),
          propertyType: 'rental',
          propertyName: '',
          propertyAddress: prop.address,
          serviceDate: '',
          annualIncome: prop.rentalIncome.toString(),
          expenses: {
            advertising: false,
            travel: false,
            maintenance: false,
            utilities: false,
          },
          additionalExpenses: '',
        });
      });
    }
    return initialProperties;
  });

  const addProperty = () => {
    const newProperty: BusinessRentalProperty = {
      id: Date.now(),
      propertyType: 'business',
      propertyName: '',
      propertyAddress: '',
      serviceDate: '',
      annualIncome: '',
      expenses: {
        advertising: false,
        travel: false,
        maintenance: false,
        utilities: false,
      },
      additionalExpenses: '',
    };
    const newProperties = [...properties, newProperty];
    setProperties(newProperties);
    updateFormData(newProperties);
  };

  const removeProperty = (id: number) => {
    const newProperties = properties.filter(prop => prop.id !== id);
    setProperties(newProperties);
    updateFormData(newProperties);
  };

  const updateProperty = (id: number, field: keyof BusinessRentalProperty, value: any) => {
    const newProperties = properties.map(prop => 
      prop.id === id ? { ...prop, [field]: value } : prop
    );
    setProperties(newProperties);
    updateFormData(newProperties);
  };

  const updateExpense = (id: number, expenseField: keyof BusinessRentalProperty['expenses'], value: boolean) => {
    const newProperties = properties.map(prop => 
      prop.id === id ? {
        ...prop,
        expenses: {
          ...prop.expenses,
          [expenseField]: value
        }
      } : prop
    );
    setProperties(newProperties);
    updateFormData(newProperties);
  };

  const updateFormData = (properties: BusinessRentalProperty[]) => {
    const businessProperties = properties.filter(p => p.propertyType === 'business');
    const rentalProperties = properties.filter(p => p.propertyType === 'rental');

    onNext({
      businessName: businessProperties[0]?.propertyName || '',
      businessType: 'sole_proprietorship', // Default value
      businessEIN: '', // This should be added to the form
      rentalProperties: rentalProperties.map(p => ({
        address: p.propertyAddress,
        rentalIncome: parseFloat(p.annualIncome) || 0,
        expenses: Object.values(p.expenses).filter(Boolean).length * 1000 // Simplified expense calculation
      }))
    });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Business or Rental Information
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Provide details about any business or rental property activities.
      </Typography>

      {properties.map((property) => (
        <Card key={property.id} sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">
                {property.propertyType === 'business' ? 'Business' : 'Rental Property'} {properties.indexOf(property) + 1}
              </Typography>
              <IconButton 
                onClick={() => removeProperty(property.id)}
                color="error"
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Property Type"
                  fullWidth
                  select
                  value={property.propertyType}
                  onChange={(e) => updateProperty(property.id, 'propertyType', e.target.value)}
                  SelectProps={{ native: true }}
                  required
                >
                  <option value="business">Business</option>
                  <option value="rental">Rental Property</option>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={property.propertyType === 'business' ? 'Business Name' : 'Property Name'}
                  fullWidth
                  value={property.propertyName}
                  onChange={(e) => updateProperty(property.id, 'propertyName', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Property Address"
                  fullWidth
                  value={property.propertyAddress}
                  onChange={(e) => updateProperty(property.id, 'propertyAddress', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date Placed in Service"
                  type="date"
                  fullWidth
                  value={property.serviceDate}
                  onChange={(e) => updateProperty(property.id, 'serviceDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Annual Income"
                  type="number"
                  fullWidth
                  value={property.annualIncome}
                  onChange={(e) => updateProperty(property.id, 'annualIncome', e.target.value)}
                  placeholder="0.00"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Expenses
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={property.expenses.advertising}
                          onChange={(e) => updateExpense(property.id, 'advertising', e.target.checked)}
                        />
                      }
                      label="Advertising"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={property.expenses.travel}
                          onChange={(e) => updateExpense(property.id, 'travel', e.target.checked)}
                        />
                      }
                      label="Business Travel"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={property.expenses.maintenance}
                          onChange={(e) => updateExpense(property.id, 'maintenance', e.target.checked)}
                        />
                      }
                      label="Maintenance & Repairs"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={property.expenses.utilities}
                          onChange={(e) => updateExpense(property.id, 'utilities', e.target.checked)}
                        />
                      }
                      label="Utilities"
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Additional Expenses"
                  fullWidth
                  multiline
                  rows={3}
                  value={property.additionalExpenses}
                  onChange={(e) => updateProperty(property.id, 'additionalExpenses', e.target.value)}
                  placeholder="Enter any additional expenses or notes here..."
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}

      <Button
        variant="contained"
        color="primary"
        onClick={addProperty}
        sx={{ mt: 2 }}
      >
        + Add {properties.length === 0 ? 'Business or Rental Property' : 'Another Property'}
      </Button>
    </Box>
  );
};

export default BusinessRental; 